import Table from '@shared/components/Table';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { basisTerminationService } from '@modules/staff/basisForTermination/services/basisTermination';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import ActionButtons from '@shared/components/ActionButtons';
import useModal from '@shared/hooks/useModal';
import ReasonForLeavingForm from './ReasonForLeavingForm';
import DeleteModal from '@shared/components/DeleteModal';
import { useDeleteHandler } from '@shared/hooks/useDeleteHandler';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function ReasonForLeaving() {
    const { t } = useTranslation();
    const { modalState, handleOpenModal, handleCloseModal } = useModal();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState('longDescription');
    const [sortDir, setSortDir] = useState('asc');
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchQuery, setSearchQuery] = useState({});
    const [searchType, setSearchType] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);
    const [data, setData] = useState([]);

    const fetchReasonLeaving = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await basisTerminationService.getReasonLeaving();
            // Keep API field names as-is
            const mappedData = Array.isArray(response) 
                ? response.map(item => ({
                    id: item.id,
                    code: item.code,
                    longDescription: item.longDescription,
                    definition: item.definition
                }))
                : [];
            setData(mappedData);
            setTotalElements(mappedData.length);
            setTotalPages(Math.ceil(mappedData.length / pageSize));
        } catch (err) {
            console.error('Error fetching reason leaving data:', err);
            setError(err?.response?.data?.message || err?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReasonLeaving();
    }, []);

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleCheckboxChange = (index) => {
        if (index >= 0 && index < data.length) {
            const record = data[index];
            const newSelection = selectedRecord?.id === record.id ? null : record;
            setSelectedRecord(newSelection);
        }
    };

    const handleAddCloseModal = () => {
        handleCloseModal();
        setSelectedRecord(null);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit' && selectedRecord) {
                await basisTerminationService.updateReasonLeaving(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await basisTerminationService.createReasonLeaving(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            setSelectedRecord(null);
            fetchReasonLeaving();
        } catch (error) {
            console.error('Error saving record:', error);
            const errorMessage = error?.response?.data?.message || error?.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    };

    const handleConfirmDelete = useDeleteHandler({
        service: basisTerminationService,
        entityName: 'ReasonLeaving',
        selectedData: selectedRecord,
        fetchData: (serviceMethod, params) => fetchReasonLeaving(), // Wrapper that matches expected signature but uses our custom fetch
        setSelectedData: setSelectedRecord,
        handleCloseModal,
        successMessage: t('common.deletedSuccessfully'),
        errorMessage: t('common.errorMessage'),
        deleteMethodName: 'deleteReasonLeaving', // Explicitly specify the delete method name
    });

   const actionButtons = (
    <ActionButtons
        buttons={[{
            ...COMMON_BUTTONS.ADD,
            onClick: () => handleOpenModal('add')
        },
        {
            ...COMMON_BUTTONS.EDIT,
            onClick: () => handleOpenModal('edit')
        },
        {
            ...COMMON_BUTTONS.DELETE,
            onClick: () => handleOpenModal('delete')
        }]}
        selectedRecord={selectedRecord}
    />
   )

    const columns = [
        { key: 'code', label: t('staff.reasonForLeaving.table.headers.code')},
        { key: 'longDescription', label: t('staff.reasonForLeaving.table.headers.name') },
        { key: 'definition', label: t('staff.reasonForLeaving.table.headers.definition') },
    ];

    return (
        <>
            <Table
                title={t('staff.reasonForLeaving.title')}
                columns={columns}
                actionButtons={actionButtons}
                data={data}
                loading={loading}
                error={error}
                selectedData={selectedRecord?.id}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchType={searchType}
                searchOptions={searchOptions}
                handleCheckboxChange={handleCheckboxChange}
                handleSelectRecord={handleSelectRecord}
                showPageSizeSelector={false}
                showPagination={true}
                showCustomSearch={true}
            />

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <ReasonForLeavingForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                />
            )}

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    name={selectedRecord?.longDescription || selectedRecord?.code}
                    entityType="record"
                />
            )}
        </>
    )   
}
